import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { api, eur, imgW } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { useSeo } from "@/lib/seo";

export function PaymentSuccess() {
  const { t, lang, clearCart } = useStore();
  const [sp] = useSearchParams();
  const sid = sp.get("session_id");
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);
  useSeo({ title: "Order confirmation" });

  useEffect(() => {
    if (!sid) { setStatus("error"); return; }
    let tries = 0, timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          const o = await api.get(`/orders/session/${sid}`);
          setOrder(o.data); setStatus("paid");
          if (!cleared.current) { cleared.current = true; clearCart(); }
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
      } catch { /* retry */ }
      if (++tries < 15) timer = setTimeout(poll, 2000); else setStatus("pending");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sid, clearCart]);

  return (
    <main className="container-x min-h-[70vh] py-20" data-testid="payment-success-page">
      {status === "checking" && <p className="flex items-center gap-3 font-mono text-sm uppercase tracking-widest" data-testid="payment-checking"><Loader2 className="h-4 w-4 animate-spin" />{t("confirming")}</p>}
      {(status === "pending" || status === "expired" || status === "error") && (
        <div data-testid="payment-pending"><h1 className="font-display text-5xl font-black uppercase">{t("paymentPending")}</h1><Link to="/checkout" className="btn-ink mt-8">{t("backToBag")}</Link></div>
      )}
      {status === "paid" && order && (
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }} className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <p className="eyebrow text-ink/50">{t("orderNo")} <span className="text-ink" data-testid="order-number">{order.order_number}</span></p>
            <h1 className="mt-4 font-display text-5xl font-black uppercase leading-[0.9] tracking-tight sm:text-7xl" data-testid="order-confirmed-title">{t("thankYou")}</h1>
            <p className="mt-6 text-ink/60">{t("confirmationSent")} <b>{order.email}</b></p>
            <div className="mt-8 text-sm leading-relaxed text-ink/70">
              <p>{order.address.first_name} {order.address.last_name}</p><p>{order.address.line1} {order.address.line2}</p>
              <p>{order.address.postal_code} {order.address.city}, {order.address.country}</p><p className="mt-2 font-mono text-xs">{order.shipping_method}</p>
            </div>
            <Link to="/shop" className="btn-ink mt-10" data-testid="continue-shopping-btn">{t("continueShopping")}</Link>
          </div>
          <div className="bg-concrete/50 p-8 lg:col-span-6">
            {order.lines.map((l) => (
              <div key={l.sku + l.size} className="flex gap-4 border-b border-ink/10 py-4">
                <img src={imgW(l.image, 200)} alt={l.name} className="h-20 w-16 object-cover" />
                <div className="flex-1 text-sm"><p className="font-medium">{l.name}</p><p className="font-mono text-xs text-ink/50">EU {l.size} × {l.qty}</p></div>
                <p className="font-mono text-sm">{eur(l.line_total, lang)}</p>
              </div>
            ))}
            <dl className="mt-6 space-y-2 text-sm">
              <div className="flex justify-between"><dt>{t("subtotal")}</dt><dd className="font-mono">{eur(order.subtotal, lang)}</dd></div>
              {order.discount > 0 && <div className="flex justify-between"><dt>{t("discount")} ({order.discount_code})</dt><dd className="font-mono">−{eur(order.discount, lang)}</dd></div>}
              <div className="flex justify-between"><dt>{t("shipping")}</dt><dd className="font-mono">{order.shipping === 0 ? t("free") : eur(order.shipping, lang)}</dd></div>
              <div className="flex justify-between border-t border-ink/10 pt-3 text-lg font-bold"><dt>{t("total")}</dt><dd className="font-mono" data-testid="order-total">{eur(order.total, lang)}</dd></div>
            </dl>
          </div>
        </motion.div>
      )}
    </main>
  );
}

export function PaymentCancel() {
  const { t } = useStore();
  return (
    <main className="container-x min-h-[60vh] py-24" data-testid="payment-cancel-page">
      <h1 className="font-display text-6xl font-black uppercase tracking-tight">{t("paymentCancelled")}</h1>
      <p className="mt-6 max-w-md text-ink/60">{t("paymentCancelledSub")}</p>
      <Link to="/checkout" className="btn-ink mt-10" data-testid="back-to-checkout-btn">{t("backToBag")}</Link>
    </main>
  );
}
